import { useState } from "react";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import { useQuery } from "react-query";
import styled from "styled-components";
import { getUser, setUserRoles, updateUserProfile, UserProfileInput, UserType } from "../../../api/admin/user";
import { addUnitMembers, changeUnitMemberRole, deleteUnitMember } from "../../../api/unit/members";
import { getOrganizationUnits } from "../../../api/decision/access";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { STab, STabList, STabPanel, STabs } from "../../../styles/Tabs";
import { SForm, SFormFooter } from "../../../styles/ChangePassword";
import { StyledField } from "../../../styles/StyledField";
import { theme } from "../../../styles/theme";
import { Role, UserRoles, userRoles } from "../../../constants/role";
import { BEErrorType } from "../../ChangePassword";
import { QueryLoader } from "../../Loader";
import { Button } from "../../Button";
import SaveIcon from "../../../assets/img/icons/save.svg";

type EditUserProps = { userId: number; refetchUsers: () => void; closeModal: () => void };

type ProfileForm = UserProfileInput & { role: string };

type Unit = { id: number; name: string };

const validationSchema = Yup.object({
	firstName: Yup.string().required("First name is required"),
	lastName: Yup.string().required("Last name is required"),
	email: Yup.string().email("Enter a valid email").required("Email is required"),
	phone: Yup.string().matches(/^[0-9]*$/, "Only digits"),
});

const SUnits = styled.div`
	font-family: ${theme.font.secondary};

	ul {
		list-style: none;
		margin: 0 0 2rem;
		padding: 0;
	}

	li {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.8rem 0;
		border-bottom: 1px solid ${theme.color.blue1};
	}

	li small {
		margin-left: 0.8rem;
		color: ${theme.color.gray};
	}

	.actions {
		display: flex;
		gap: 0.4rem;
	}

	.add {
		display: flex;
		gap: 1rem;
		align-items: flex-end;
	}

	.add > div {
		flex: 1;
	}

	select {
		height: 3.6rem;
	}
`;

const fieldError = (beErrors: BEErrorType[] | undefined, field: string) =>
	beErrors?.filter((error) => error.path === `children[${field}].data` || error.path === `data.${field}`).map((error) => error.message);

/** Organization admins edit a user's profile, organization role and unit memberships. */
export const EditUser = (props: EditUserProps) => {
	const { userId, refetchUsers, closeModal } = props;
	const { axiosPrivate } = useAxiosPrivate();
	const [beErrors, setBeErrors] = useState<BEErrorType[] | undefined>(undefined);
	const [unitToAdd, setUnitToAdd] = useState("");
	const [roleToAdd, setRoleToAdd] = useState<string>(Role.member);
	const [busy, setBusy] = useState(false);

	const { isLoading, isError, data: user, refetch } = useQuery<UserType>(["user", userId], () => getUser(axiosPrivate, userId));
	const { data: units = [] } = useQuery<Unit[]>("organization-units-flat", () => getOrganizationUnits(axiosPrivate));

	const refresh = () => {
		refetch();
		refetchUsers();
	};

	if (isLoading) {
		return <QueryLoader />;
	}
	if (isError || !user) {
		return (
			<>
				<h3>User not found</h3>
				<SFormFooter>
					<Button onClick={closeModal}>Close</Button>
				</SFormFooter>
			</>
		);
	}

	const currentRole = user.roles.includes(UserRoles.ROLE_ADMIN) ? UserRoles.ROLE_ADMIN : UserRoles.ROLE_USER;
	const initialValues: ProfileForm = {
		firstName: user.firstName ?? "",
		lastName: user.lastName ?? "",
		email: user.email ?? "",
		phone: user.phone ?? "",
		role: currentRole,
	};

	const saveProfile = async (values: ProfileForm) => {
		setBeErrors(undefined);
		const { role, ...profile } = values;
		const changed = (Object.keys(profile) as (keyof UserProfileInput)[]).filter((key) => profile[key] !== initialValues[key]);
		if (changed.length) {
			const res = await updateUserProfile({
				axiosPrivate,
				userId,
				data: Object.fromEntries(changed.map((key) => [key, profile[key]])),
			});
			if (res?.status === 400) {
				setBeErrors(res.data?.fieldErrors);
				return;
			}
		}
		if (role !== currentRole) {
			await setUserRoles({ axiosPrivate, userId, roles: [role] });
		}
		refresh();
	};

	const memberships = user.organizationUnits ?? [];
	const memberUnitIds = memberships.map((membership) => membership.organizationUnit.id);
	const isUnitAdmin = (roles: string[]) => roles.includes(Role.admin);

	const run = async (request: Promise<unknown>) => {
		setBusy(true);
		try {
			await request;
		} finally {
			setBusy(false);
		}
	};

	const addToUnit = () =>
		run(
			addUnitMembers(axiosPrivate, refresh, Number(unitToAdd), {
				roles: roleToAdd === Role.admin ? [Role.member, Role.admin] : [Role.member],
				users: [userId],
			})
		).then(() => setUnitToAdd(""));

	return (
		<>
			<h3>
				{user.firstName} {user.lastName}
			</h3>
			<STabs selectedTabClassName="is-selected" selectedTabPanelClassName="is-selected">
				<STabList>
					<STab>Profile</STab>
					<STab>Units</STab>
				</STabList>
				<STabPanel>
					<Formik initialValues={initialValues} onSubmit={saveProfile} validationSchema={validationSchema} enableReinitialize>
						{({ errors, touched, isValid, isSubmitting, dirty }) => (
							<SForm>
								{(["firstName", "lastName", "email", "phone"] as const).map((name) => (
									<StyledField key={name}>
										<label htmlFor={`edit-user-${name}`}>
											{{ firstName: "First name", lastName: "Last name", email: "Email", phone: "Phone" }[name]}
										</label>
										<Field id={`edit-user-${name}`} name={name} type={name === "email" ? "email" : "text"} />
										{errors[name] && touched[name] ? <span>{errors[name]}</span> : null}
										{fieldError(beErrors, name)?.map((message, i) => (
											<span key={i}>{message}</span>
										))}
									</StyledField>
								))}
								<StyledField>
									<label htmlFor="edit-user-role">Organization role</label>
									<Field as="select" id="edit-user-role" name="role">
										{userRoles.map((role) => (
											<option key={role.value} value={role.value}>
												{role.label}
											</option>
										))}
									</Field>
								</StyledField>
								<SFormFooter>
									<Button type="reset" onClick={closeModal}>
										Close
									</Button>
									<Button type="submit" disabled={!isValid || isSubmitting || !dirty} icon={SaveIcon}>
										Save
									</Button>
								</SFormFooter>
							</SForm>
						)}
					</Formik>
				</STabPanel>
				<STabPanel>
					<SUnits>
						{memberships.length === 0 ? (
							<p>Not a member of any unit.</p>
						) : (
							<ul>
								{memberships.map(({ organizationUnit, roles }) => (
									<li key={organizationUnit.id}>
										<span>
											{organizationUnit.name}
											<small>{isUnitAdmin(roles) ? "Unit admin" : "Member"}</small>
										</span>
										<span className="actions">
											<Button
												small
												bg={false}
												disabled={busy}
												onClick={() =>
													run(
														changeUnitMemberRole(axiosPrivate, refresh, organizationUnit.id, userId, {
															roles: isUnitAdmin(roles) ? [Role.member] : [Role.member, Role.admin],
														})
													)
												}
											>
												{isUnitAdmin(roles) ? "Remove unit admin" : "Make unit admin"}
											</Button>
											<Button
												small
												bg={false}
												disabled={busy}
												onClick={() => run(deleteUnitMember(axiosPrivate, refresh, organizationUnit.id, userId))}
											>
												Remove from unit
											</Button>
										</span>
									</li>
								))}
							</ul>
						)}
						<div className="add">
							<StyledField>
								<label htmlFor="edit-user-unit">Add to unit</label>
								<select id="edit-user-unit" value={unitToAdd} onChange={(e) => setUnitToAdd(e.target.value)}>
									<option value="">Choose a unit</option>
									{units
										.filter((unit) => !memberUnitIds.includes(unit.id))
										.map((unit) => (
											<option key={unit.id} value={unit.id}>
												{unit.name}
											</option>
										))}
								</select>
							</StyledField>
							<StyledField>
								<label htmlFor="edit-user-unit-role">As</label>
								<select id="edit-user-unit-role" value={roleToAdd} onChange={(e) => setRoleToAdd(e.target.value)}>
									<option value={Role.member}>Member</option>
									<option value={Role.admin}>Unit admin</option>
								</select>
							</StyledField>
							<Button small disabled={!unitToAdd || busy} onClick={addToUnit}>
								Add
							</Button>
						</div>
					</SUnits>
				</STabPanel>
			</STabs>
		</>
	);
};
